"use client"

import { useEffect, useRef, useState } from "react"
import {
  initializeSocket,
  disconnectSocket,
  addEventListener,
  getConnectionStatus,
  isSocketIOSupported,
} from "@/lib/socket"
import type { SocketEventCallback } from "@/lib/socket"

export function useSocket() {
  const socketRef = useRef<any>(null)
  const [connectionStatus, setConnectionStatus] = useState<string>("disconnected")
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    // Verificar se Socket.IO é suportado
    if (!isSocketIOSupported()) {
      console.log("Socket.IO não é suportado neste ambiente")
      return
    }

    // Inicializar socket apenas no cliente
    if (typeof window !== "undefined" && !isInitialized) {
      setIsInitialized(true)

      const initSocket = async () => {
        try {
          socketRef.current = await initializeSocket()

          // Monitorar status da conexão
          const statusInterval = setInterval(() => {
            const status = getConnectionStatus()
            setConnectionStatus(status)
          }, 2000) // Verificar a cada 2 segundos

          return () => {
            clearInterval(statusInterval)
            if (socketRef.current) {
              disconnectSocket()
            }
          }
        } catch (error) {
          console.error("Erro ao inicializar Socket.IO:", error)
          setConnectionStatus("disconnected")
        }
      }

      initSocket()
    }

    return () => {
      // Não desconectar o socket global ao desmontar o hook
      // O socket deve ser persistente enquanto a aplicação estiver rodando
      if (socketRef.current) {
        // Apenas limpar referência local se necessário
      }
    }
  }, [isInitialized])

  return {
    socket: socketRef.current,
    isConnected: connectionStatus === "connected",
    connectionStatus,
    isSupported: isSocketIOSupported(),
  }
}

export function useSocketEvent<T = any>(event: string, callback: (data: T) => void) {
  const { socket, isConnected } = useSocket()

  useEffect(() => {
    // Só adicionar listener se Socket.IO estiver conectado
    if (socket && isConnected) {
      const eventCallback: SocketEventCallback = callback
      const removeListener = addEventListener(event, eventCallback)

      return removeListener
    }
  }, [socket, isConnected, event, callback])
}
